import React, { useState, useMemo } from "react";
import { getTimeZones } from "@vvo/tzdb";
import { lowerCase } from "lodash";

import { CpSelectSingle } from "@components";
import { CpSelect } from "../cp-select/cp-select.component";

type TimezoneOption = {
  name: string;
  value: string;
  alternativeName?: string;
};

type TransformedTimezoneOption = TimezoneOption & {
  id: string;
  readableName: string;
};

const timeZones: TimezoneOption[] = getTimeZones().map((tz) => ({
  name: `${tz.name} (${tz.alternativeName})`,
  value: tz.name,
}));

const initialTimezones: TimezoneOption[] = [
  { name: "America/Los_Angeles (Pacific Time)", value: "America/Los_Angeles" },
  { name: "America/Denver (Mountain Time)", value: "America/Denver" },
  { name: "America/Chicago (Central Time)", value: "America/Chicago" },
  { name: "America/New_York (Eastern Time)", value: "America/New_York" },
  { name: "America/Phoenix (Mountain Time)", value: "America/Phoenix" },
  { name: "America/Anchorage (Alaska Time)", value: "America/Anchorage" },
  {
    name: "Pacific/Honolulu (Hawaii-Aleutian Time)",
    value: "Pacific/Honolulu",
  },
  { name: "America/Halifax (Atlantic)", value: "America/Halifax" },
  { name: "America/St_Johns (Newfoundland Time)", value: "America/St_Johns" },
];

type CpTimezoneProps = {
  onChange: (value: string | null) => void;
  value?: string | null;
  contentWidth?: string;
  position?: string; // TODO: Type this properly once CpSelectSingle is migrated
  disabled?: boolean;
  clearable?: boolean;
};

export function CpTimezone({
  onChange,
  value,
  contentWidth,
  position,
  disabled,
  clearable = false,
}: CpTimezoneProps) {
  const valObj = useMemo(() => {
    if (!value) return null;
    return timeZones.find((tz) => tz.value === value);
  }, [value]);

  const [search, setSearch] = useState("");
  return (
    <CpSelectSingle
      clearable={clearable}
      contentWidth={contentWidth}
      data={search ? timeZones : initialTimezones}
      disabled={disabled}
      onChange={(val: TimezoneOption | null) => onChange(val?.value ?? null)}
      placeholder="Select time zone"
      position={position}
      searchFilter={
        (CpSelect as any).filterAlpha(search, ["name", "readableName"]) as (
          item: any,
        ) => boolean
      }
      searchOnChange={setSearch}
      searchValue={search}
      transformData={(tz: TimezoneOption): TransformedTimezoneOption => ({
        ...tz,
        id: tz.value,
        // lowerCase allows for better searching (eg: "new york" instead of "new_york")
        readableName: `${lowerCase(tz.name)} (${tz.alternativeName})`,
      })}
      value={valObj}
      {...({} as any)} // TODO: Remove when CpSelectSingle is migrated to TypeScript
    />
  );
}
